import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, Store } from "lucide-react";
import { PricingProposal } from "@/lib/api";

// Shopify Basic plan fees (configurable)
const SHOPIFY_RATE = 0.029;   // 2.9%
const SHOPIFY_FIXED = 0.30;   // $0.30

function shopifyFee(price: number) {
  return price * SHOPIFY_RATE + SHOPIFY_FIXED;
}

function fmt(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function clamp(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), max);
}

function applyRounding(v: number): number {
  const dollars = Math.floor(v);
  const cents = v - dollars;
  return cents > 0.5 ? dollars + 0.99 : Math.max(dollars - 1 + 0.99, v);
}

type MarginMode = "markup" | "gross" | "net";

interface Props {
  proposal: PricingProposal;
  initialPrice?: number;
  onApply: (price: number, compareAt?: number) => Promise<void>;
}

export function PricingSlider({ proposal, initialPrice, onApply }: Props) {
  const { cost, floor: rawFloor, ceiling: rawCeiling, map_enforced } = proposal;

  // When cost-based floor exceeds MSRP ceiling, decouple them:
  // allow the slider to roam cost+1% → cost+60%, but warn the user.
  const floorAboveCeiling = rawFloor >= rawCeiling && rawCeiling > 0;
  const sliderMin = floorAboveCeiling ? Math.round(cost * 1.01 * 100) / 100 : rawFloor;
  const sliderMax = floorAboveCeiling ? Math.round(cost * 1.60 * 100) / 100 : Math.max(rawCeiling, rawFloor * 1.5);

  // Margin mode: markup (cost-based) | gross (revenue-based) | net (after Shopify fees)
  const [marginMode, setMarginMode] = useState<MarginMode>("gross");
  const [marginPct, setMarginPct] = useState(() => {
    // If a strategy preset injected a price, back-calculate the gross margin %
    if (initialPrice && initialPrice > cost) {
      const backCalc = ((initialPrice - cost) / initialPrice) * 100;
      return clamp(Math.round(backCalc * 2) / 2, 1, 60);
    }
    const initial = Math.round(proposal.target_margin * 100) || 20;
    return clamp(initial, 1, 60);
  });
  const [unitsPerMonth, setUnitsPerMonth] = useState(() => {
    try { return Number(localStorage.getItem("pricingUnitsPerMonth") || "5"); } catch { return 5; }
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Compute proposed price based on margin mode
  function priceFromMargin(pct: number): number {
    if (marginMode === "markup") {
      // markup: price = cost * (1 + pct/100)
      return applyRounding(clamp(cost * (1 + pct / 100), sliderMin, sliderMax));
    } else {
      // gross/net margin: price = cost / (1 - pct/100)
      if (pct >= 100) return sliderMax;
      return applyRounding(clamp(cost / (1 - pct / 100), sliderMin, sliderMax));
    }
  }

  const proposed = priceFromMargin(marginPct);
  const fee = shopifyFee(proposed);
  const grossMargin = proposed - cost;
  const netProfit = grossMargin - fee;

  // Display margin depends on mode
  const displayMargin =
    marginMode === "markup"
      ? cost > 0 ? ((proposed - cost) / cost) * 100 : 0
      : marginMode === "gross"
      ? proposed > 0 ? ((proposed - cost) / proposed) * 100 : 0
      : proposed > 0 ? (netProfit / proposed) * 100 : 0;

  // Floor column stats
  const floorFee = shopifyFee(rawFloor);
  const floorGross = rawFloor - cost;
  const floorNet = floorGross - floorFee;
  const floorDisplayMargin =
    marginMode === "markup"
      ? cost > 0 ? ((rawFloor - cost) / cost) * 100 : 0
      : marginMode === "gross"
      ? rawFloor > 0 ? ((rawFloor - cost) / rawFloor) * 100 : 0
      : rawFloor > 0 ? (floorNet / rawFloor) * 100 : 0;

  // Visual track
  const range = sliderMax - sliderMin;
  const proposedPct = range > 0 ? clamp(((proposed - sliderMin) / range) * 100, 0, 100) : 50;

  const handleSlider = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMarginPct(Number(e.target.value));
    setSaved(false);
  }, []);

  const handleUnits = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Math.max(1, Number(e.target.value) || 1);
    setUnitsPerMonth(n);
    try { localStorage.setItem("pricingUnitsPerMonth", String(n)); } catch { /**/ }
  }, []);

  const handleApply = useCallback(async () => {
    setSaving(true);
    try {
      await onApply(proposed, rawCeiling > 0 ? rawCeiling : undefined);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }, [proposed, rawCeiling, onApply]);

  const marginLabel =
    marginMode === "markup" ? "Markup %" : marginMode === "gross" ? "Gross margin %" : "Net margin %";

  return (
    <div className="space-y-5">
      {/* Floor > ceiling warning */}
      {floorAboveCeiling && (
        <div className="flex items-center gap-2 text-xs text-amber-500 bg-amber-500/10 rounded-md px-3 py-2">
          <Store className="h-3.5 w-3.5 shrink-0" />
          Cost ({fmt(cost)}) exceeds MSRP ({fmt(rawCeiling)}). MAP floor removed — price freely above MSRP.
        </div>
      )}

      {/* Margin mode toggle */}
      <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted w-fit">
        {(["markup", "gross", "net"] as MarginMode[]).map(m => (
          <button
            key={m}
            onClick={() => { setMarginMode(m); setSaved(false); }}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              marginMode === m
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {m === "markup" ? "Markup" : m === "gross" ? "Gross" : "Net (after fees)"}
          </button>
        ))}
      </div>

      {/* Price track */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-mono tabular-nums">Cost {fmt(cost)}</span>
          {map_enforced && !floorAboveCeiling && (
            <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px]">MAP enforced</Badge>
          )}
          <span className="font-mono tabular-nums">
            {floorAboveCeiling ? `Max ${fmt(sliderMax)}` : `MSRP ${fmt(rawCeiling)}`}
          </span>
        </div>
        <div className="relative h-3 rounded-full bg-gradient-to-r from-rose-200 via-emerald-100 to-slate-200">
          {!floorAboveCeiling && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-amber-500 rounded"
              style={{ left: "0%" }}
              title={`MAP floor: ${fmt(rawFloor)}`}
            />
          )}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white shadow-md transition-all duration-75"
            style={{ left: `calc(${proposedPct}% - 8px)` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs font-mono tabular-nums">
          <span className="text-amber-600">{fmt(sliderMin)}</span>
          <span className="text-emerald-600 font-semibold text-sm">{fmt(proposed)}</span>
          <span className="text-muted-foreground">{fmt(sliderMax)}</span>
        </div>
      </div>

      {/* Margin slider */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs text-muted-foreground uppercase tracking-wide">{marginLabel}</label>
          <span className="text-sm font-semibold font-mono tabular-nums text-emerald-600">
            {displayMargin.toFixed(1)}%
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={60}
          step={0.5}
          value={marginPct}
          onChange={handleSlider}
          className="w-full h-2 rounded-full accent-emerald-500 cursor-pointer"
          aria-label={marginLabel}
        />
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>1%</span>
          <span>60%</span>
        </div>
      </div>

      {/* Revenue scenario table */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground uppercase tracking-wide">Revenue scenarios</span>
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-xs text-muted-foreground">Units/mo:</span>
            <Input
              type="number"
              min={1}
              max={9999}
              value={unitsPerMonth}
              onChange={handleUnits}
              className="w-16 h-6 text-xs text-center px-1 font-mono"
            />
          </div>
        </div>
        <table className="w-full text-xs tabular-nums font-mono">
          <thead>
            <tr className="text-muted-foreground border-b border-border">
              <th className="text-left py-1 font-normal">Metric</th>
              <th className="text-right py-1 font-normal">At floor</th>
              <th className="text-right py-1 font-semibold text-emerald-600">At {marginPct.toFixed(1)}%</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/50">
              <td className="py-1 text-muted-foreground">Sale price</td>
              <td className="text-right py-1">{fmt(rawFloor)}</td>
              <td className="text-right py-1 text-emerald-600 font-medium">{fmt(proposed)}</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-1 text-muted-foreground">Gross margin</td>
              <td className="text-right py-1">{fmt(floorGross)}</td>
              <td className="text-right py-1 text-emerald-600 font-medium">{fmt(grossMargin)}</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-1 text-muted-foreground flex items-center gap-1">
                <Store className="h-3 w-3 inline-block" /> Shopify fee
              </td>
              <td className="text-right py-1 text-rose-500">−{fmt(floorFee)}</td>
              <td className="text-right py-1 text-rose-500">−{fmt(fee)}</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-1 font-medium">Net profit / unit</td>
              <td className={`text-right py-1 font-medium ${floorNet < 0 ? "text-rose-500" : ""}`}>{fmt(floorNet)}</td>
              <td className={`text-right py-1 font-medium ${netProfit < 0 ? "text-rose-500" : "text-emerald-600"}`}>{fmt(netProfit)}</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-1 text-muted-foreground">{marginLabel}</td>
              <td className="text-right py-1">{floorDisplayMargin.toFixed(1)}%</td>
              <td className="text-right py-1 text-emerald-600 font-medium">{displayMargin.toFixed(1)}%</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-1 text-muted-foreground">Monthly rev*</td>
              <td className="text-right py-1">{fmt(rawFloor * unitsPerMonth)}</td>
              <td className="text-right py-1 text-emerald-600 font-medium">{fmt(proposed * unitsPerMonth)}</td>
            </tr>
            <tr>
              <td className="py-1 text-muted-foreground">Monthly net GP*</td>
              <td className={`text-right py-1 ${floorNet < 0 ? "text-rose-500" : ""}`}>{fmt(floorNet * unitsPerMonth)}</td>
              <td className={`text-right py-1 font-medium ${netProfit < 0 ? "text-rose-500" : "text-emerald-600"}`}>{fmt(netProfit * unitsPerMonth)}</td>
            </tr>
          </tbody>
        </table>
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>* at {unitsPerMonth} units/mo (editable above)</span>
          <span className="flex items-center gap-1">
            <Store className="h-3 w-3" />
            Shopify fee: {(SHOPIFY_RATE * 100).toFixed(1)}% + {fmt(SHOPIFY_FIXED)}
          </span>
        </div>
      </div>

      {/* Apply CTA */}
      <Button
        onClick={handleApply}
        disabled={saving}
        variant="ember"
        size="sm"
        className="w-full"
      >
        {saving
          ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Applying…</>
          : saved
          ? "✓ Applied"
          : `Apply ${fmt(proposed)} (${displayMargin.toFixed(1)}% ${marginMode === "markup" ? "markup" : marginMode === "gross" ? "gross" : "net"})`}
      </Button>
    </div>
  );
}
